import { useState, type ReactNode } from "react";
import { useAuth } from "react-oidc-context";
import { useQuery } from "@tanstack/react-query";
import { FarmacenterCross } from "@moodstudios/farmacenter-design";
import { agruparPermisos } from "../permisos";
import { EtiquetaCategoria } from "../ui/primitivas";
import { Avatar } from "../ui/Avatar";
import { NOVA_PANEL_HOST } from "../asistente/Asistente";
import { NovaTerminal } from "../asistente/NovaTerminal";
import { useAsistenteApi } from "../asistente/asistenteApi";
import { useUsuariosApi } from "./usuarios/usuariosApi";

/**
 * El Panel de inicio: la casa de Nova. Arriba, "Tu sesión" (quién eres, qué puedes hacer y —en
 * breve— métricas en vivo del ERP). Debajo, Nova a pantalla completa: el chat estilo ChatGPT y el
 * lienzo se dibujan aquí (la propia burbuja de Nova se porta a este hueco). Al salir del Panel, todo
 * se colapsa a la burbuja de abajo a la derecha sin perder el progreso.
 */
export function Panel() {
  const auth = useAuth();
  const perfil = auth.user?.profile;
  const nombre = perfil?.name ?? perfil?.preferred_username ?? "";

  // Permisos efectivos: unión de los del token (claim scope) y los que otorga el rol. Así el
  // resumen es correcto antes y después del "flip" (cuando los scopes dejan de venir por defecto).
  const api = useUsuariosApi();
  const presetsQ = useQuery({
    queryKey: ["usuarios-presets"],
    queryFn: () => api.presets(),
    staleTime: 60_000,
  });
  const rolesRealm = (
    (perfil as { realm_access?: { roles?: string[] } } | undefined)?.realm_access?.roles ?? []
  ).filter((r) => r.startsWith("farmacenter-"));
  const scopesDelRol = (presetsQ.data ?? [])
    .filter((p) => rolesRealm.includes(p.roleName))
    .flatMap((p) => p.scopes);
  const asistApi = useAsistenteApi();

  // Permisos EFECTIVOS del servidor (whoami): la fuente autoritativa. Si aún no está, cae al cálculo
  // desde el token + rol (que puede dar 0 en cuentas cuyos scopes vienen sólo del rol).
  const whoamiQ = useQuery({
    queryKey: ["whoami"],
    queryFn: () => asistApi.permisos(),
    staleTime: 5 * 60_000,
    retry: false,
  });
  const alcancesToken = (auth.user?.scope ?? "").split(" ").filter((s) => s.includes("."));
  const alcancesServidor = whoamiQ.data?.scopes ?? [];
  const alcances =
    alcancesServidor.length > 0
      ? [...new Set(alcancesServidor)]
      : [...new Set([...alcancesToken, ...scopesDelRol])];
  const gruposPermisos = agruparPermisos(alcances);
  const puedeGestionar = alcances.filter((s) => !s.endsWith(".read")).length;

  const [verPermisos, setVerPermisos] = useState(false);
  const [verLog, setVerLog] = useState(false);

  // Conectados al ERP en vivo (presencia por latidos). Degrada a lista vacía si aún no está.
  const conectadosQ = useQuery({
    queryKey: ["nova-presencia"],
    queryFn: () => asistApi.presencia(),
    refetchInterval: 30_000,
    staleTime: 25_000,
    retry: false,
  });
  const conectados = conectadosQ.data?.conectados ?? [];

  return (
    <div className="flex h-full min-h-0 flex-col gap-3">
      <header className="flex flex-none items-center gap-2.5">
        <FarmacenterCross size={26} title="Farmacenter" />
        <h1 className="fc-titulo-fade font-brand text-xl font-bold tracking-tight">
          Hola{nombre ? `, ${nombre.split(" ")[0]}` : ""} — soy Nova
        </h1>
      </header>

      {/* Tu sesión: tira compacta de tarjetas + permisos. */}
      <section className="flex-none rounded-panel border border-border bg-surface p-3">
        <div className="mb-3 flex items-center justify-between gap-3">
          <h2 className="font-brand text-base font-bold">Tu sesión</h2>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setVerLog((v) => !v)}
              className="text-xs font-medium text-text-muted hover:text-text"
            >
              {verLog ? "Ocultar actividad" : "Ver actividad"}
            </button>
            <button
              type="button"
              onClick={() => setVerPermisos((v) => !v)}
              className="text-xs font-medium text-text-muted hover:text-text"
            >
              {verPermisos ? "Ocultar permisos" : `Ver permisos (${alcances.length})`}
            </button>
          </div>
        </div>

        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
          <Tarjeta etiqueta="Usuario">
            <p className="truncate font-brand text-base font-bold leading-tight">{nombre || "—"}</p>
            <p className="truncate text-2xs text-text-muted">{perfil?.email ?? "—"}</p>
          </Tarjeta>
          <Tarjeta etiqueta="Qué puedes hacer">
            <p className="font-brand text-base font-bold leading-tight">
              {alcances.length} <span className="text-xs font-medium text-text-muted">permisos</span>
            </p>
            <p className="text-2xs text-text-muted">{puedeGestionar} de gestión · resto solo ver</p>
          </Tarjeta>
          <TarjetaPronto etiqueta="Cajas / POS abiertas" nota="En vivo, muy pronto" />
          <TarjetaConectados conectados={conectados} />
        </div>

        {verPermisos ? (
          <div className="mt-4 flex max-h-48 flex-col gap-3 overflow-y-auto border-t border-border pt-3">
            {alcances.length === 0 ? (
              <span className="text-sm text-text-muted">—</span>
            ) : (
              gruposPermisos.map((g) => (
                <div key={g.area} className="flex flex-col gap-1.5">
                  <EtiquetaCategoria>{g.areaLabel}</EtiquetaCategoria>
                  <div className="flex flex-wrap gap-1.5">
                    {g.permisos.map((p) => {
                      const soloLectura = p.accion === "read";
                      return (
                        <span
                          key={p.scope}
                          title={p.scope}
                          className={`inline-flex items-center gap-1.5 rounded-pill border bg-surface-raised px-2.5 py-1 text-xs ${
                            soloLectura ? "border-border text-text-muted" : "border-border-control text-text"
                          }`}
                        >
                          <span
                            aria-hidden
                            className="h-1.5 w-1.5 flex-none rounded-full"
                            style={{
                              background: soloLectura
                                ? "var(--fc-color-text-muted)"
                                : "var(--fc-color-action, var(--fc-color-text-muted))",
                            }}
                          />
                          {p.titulo}
                        </span>
                      );
                    })}
                  </div>
                </div>
              ))
            )}
          </div>
        ) : null}
      </section>

      {verLog ? <NovaTerminal className="h-44 flex-none" /> : null}

      {/* Nova a pantalla completa: el chat y el lienzo se portan a este hueco (misma instancia). */}
      <div id={NOVA_PANEL_HOST} className="min-h-0 flex-1" />
    </div>
  );
}

function Tarjeta({ etiqueta, children }: { etiqueta: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5 rounded-control border border-border bg-surface-raised px-3 py-2">
      <p className="font-data text-2xs uppercase tracking-wider text-text-muted">{etiqueta}</p>
      {children}
    </div>
  );
}

function TarjetaPronto({ etiqueta, nota }: { etiqueta: string; nota: string }) {
  return (
    <div className="flex flex-col gap-0.5 rounded-control border border-dashed border-border bg-surface-raised px-3 py-2 opacity-80">
      <p className="font-data text-2xs uppercase tracking-wider text-text-muted">{etiqueta}</p>
      <p className="text-xs text-text-muted">{nota}</p>
    </div>
  );
}

/** Conectados al ERP en vivo: avatares de iniciales superpuestos (nombre al pasar el ratón) + total. */
function TarjetaConectados({ conectados }: { conectados: { id: string; nombre: string }[] }) {
  const visibles = conectados.slice(0, 5);
  const resto = conectados.length - visibles.length;
  return (
    <div className="flex flex-col gap-1 rounded-control border border-border bg-surface-raised px-3 py-2">
      <p className="font-data text-2xs uppercase tracking-wider text-text-muted">Conectados al ERP</p>
      {conectados.length === 0 ? (
        <p className="text-xs text-text-muted">Nadie más ahora mismo</p>
      ) : (
        <div className="flex items-center gap-2">
          <div className="flex -space-x-2">
            {visibles.map((c) => (
              <Avatar key={c.id} nombre={c.nombre} size={24} anillo title={c.nombre} />
            ))}
          </div>
          {resto > 0 ? <span className="text-xs font-medium text-text-muted">+{resto}</span> : null}
          <span className="ml-auto font-brand text-base font-bold leading-none">{conectados.length}</span>
        </div>
      )}
    </div>
  );
}
